import { styled } from '@mui/material/styles'
import { Helmet } from 'react-helmet-async'
import { spacing } from '@mui/system'
import { Box, Button, Divider as MuiDivider, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ArrowBack } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { MouseEvent } from 'react'

const Divider = styled(MuiDivider)(spacing)

export interface PageTitleProps {
  children: string | JSX.Element
}

export function PageTitle({ children }: PageTitleProps) {
  return (
    <Typography
      variant="h3"
      display="inline"
      sx={{
        textWrap: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden'
      }}
      title={typeof children === 'string' ? children : undefined}
    >
      {children}
    </Typography>
  )
}

export interface PageHeaderProps {
  title: string
  heading?: string | JSX.Element
  breadcrumbs?: JSX.Element
  action?: JSX.Element
  hideBackButton?: boolean
  backButton?: JSX.Element
  onBackButtonClick?: (event: MouseEvent<HTMLButtonElement>) => void
  disableGutters?: boolean
}

export default function PageHeader({
  title,
  heading,
  breadcrumbs,
  action,
  hideBackButton = false,
  backButton,
  onBackButtonClick,
  disableGutters = false
}: PageHeaderProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The navigation function.
   */
  const navigate = useNavigate()

  /**
   * Handle back button click event.
   */
  function handleBackButtonClick(event: MouseEvent<HTMLButtonElement>) {
    if (onBackButtonClick) {
      onBackButtonClick(event)
    } else {
      navigate(-1)
    }
  }

  return (
    <Box>
      <Helmet title={title} />

      {!hideBackButton && (
        <>
          {backButton ? (
            <>{backButton}</>
          ) : (
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBackButtonClick}>
              {t('back')}
            </Button>
          )}

          <Divider my={6} />
        </>
      )}

      <Stack direction="column">
        {breadcrumbs && <Box mb={6}>{breadcrumbs}</Box>}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent={action ? 'space-between' : 'flex-start'}
          gap={2}
        >
          <Box
            sx={{
              flexShrink: 1,
              flexGrow: 0,
              width: '100%',
              maxWidth: action ? 'calc(100% - 48px)' : '100%'
            }}
          >
            {heading && typeof heading !== 'string' ? (
              <>{heading}</>
            ) : (
              <PageTitle>{heading ?? title}</PageTitle>
            )}
          </Box>

          {action && <Box sx={{ flexShrink: 1, flexGrow: 0 }}>{action}</Box>}
        </Stack>
      </Stack>

      <Divider mt={6} mb={disableGutters ? 0 : 6} />
    </Box>
  )
}
